import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import * as styles from './calendar-scheduler.css';
import { calendarAutoScrollOptions } from './auto-scroll';
import { EventItem } from './event-item';
import type { CalendarEvent, EventRenderer, EventResizeHandler } from './types';
import { calculateEventLayouts, type EventLayout } from './utils';

interface EventLayerProps {
  events: CalendarEvent[];
  onEventEdit: (event: CalendarEvent) => void;
  onEventUpdate: (event: CalendarEvent) => void;
  onEventDelete: (id: string) => void;
  renderEvent?: EventRenderer;
  dragSnapMinutes?: number;
}

export const EventLayer: React.FC<EventLayerProps> = ({
  events,
  onEventEdit,
  onEventUpdate,
  onEventDelete: _onEventDelete,
  renderEvent,
  dragSnapMinutes = 15,
}) => {
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [activeEventDimensions, setActiveEventDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  
  // Optimistic update support
  const committedEventRef = useRef<CalendarEvent | null>(null);
  const [updateTick, forceUpdate] = useState({});

  // Merge props with committed local state to prevent "flash" to old position
  const mergedEvents = useMemo(() => {
    const committed = committedEventRef.current;
    if (!committed) return events;

    // Check if the prop update has arrived
    const propEvent = events.find(e => e.id === committed.id);
    if (propEvent && propEvent.startTime === committed.startTime && propEvent.endTime === committed.endTime) {
      // Data is synced, clear committed ref
      committedEventRef.current = null;
      return events;
    }

    return events.map(e => (e.id === committed.id ? { ...e, ...committed } : e));
  }, [events, updateTick]); // Re-run when props change or component re-renders (via updateTick)

  const eventLayouts = useMemo(() => calculateEventLayouts(mergedEvents), [mergedEvents]);
  const layoutMap = useMemo(() => {
    const map = new Map<string, EventLayout>();
    for (const layout of eventLayouts) {
      map.set(layout.event.id, layout);
    }
    return map;
  }, [eventLayouts]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getDraggedEventUpdate = useCallback(
    (eventData: CalendarEvent, delta: DragMoveEvent['delta']) => {
      if (!layerRef.current) return null;
      const layerRect = layerRef.current.getBoundingClientRect();
      const totalHeight = layerRect.height;
      if (totalHeight <= 0) return null;

      const totalMinutes = 24 * 60;
      const snapInterval =
        Number.isFinite(dragSnapMinutes) && dragSnapMinutes > 0
          ? dragSnapMinutes
          : 15;

      const startParts = eventData.startTime.split(':').map(Number);
      const endParts = eventData.endTime.split(':').map(Number);
      const startHour = startParts[0];
      const startMinute = startParts[1];
      const endHour = endParts[0];
      const endMinute = endParts[1];
      if (
        startHour === undefined ||
        startMinute === undefined ||
        endHour === undefined ||
        endMinute === undefined ||
        !Number.isFinite(startHour) ||
        !Number.isFinite(startMinute) ||
        !Number.isFinite(endHour) ||
        !Number.isFinite(endMinute)
      ) {
        return null;
      }

      const durationMinutes =
        endHour * 60 + endMinute - (startHour * 60 + startMinute);

      const dragMinutes = (delta.y / totalHeight) * totalMinutes;
      const startMinutes = startHour * 60 + startMinute;
      const snappedStartMinutes =
        Math.round((startMinutes + dragMinutes) / snapInterval) * snapInterval;

      let newStartMinutes = snappedStartMinutes;

      if (newStartMinutes < 0) {
        newStartMinutes = 0;
      }

      if (newStartMinutes + durationMinutes > totalMinutes) {
        newStartMinutes = totalMinutes - durationMinutes;
      }

      const newStartHour = Math.floor(newStartMinutes / 60);
      const newStartMin = newStartMinutes % 60;
      const newEndMinutes = newStartMinutes + durationMinutes;
      const newEndHour = Math.floor(newEndMinutes / 60) % 24;
      const newEndMin = newEndMinutes % 60;

      const newStartTime = `${newStartHour.toString().padStart(2, '0')}:${newStartMin.toString().padStart(2, '0')}`;
      const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;

      if (
        newStartTime === eventData.startTime &&
        newEndTime === eventData.endTime
      ) {
        return null;
      }

      return { startTime: newStartTime, endTime: newEndTime };
    },
    [dragSnapMinutes]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const eventData = event.active.data.current?.['event'] as
      | CalendarEvent
      | undefined;
    if (eventData) {
      setActiveEvent(eventData);
      const element = layerRef.current?.querySelector(
        `[data-event-id="${eventData.id}"]`
      ) as HTMLElement | null;
      if (element) {
        const rect = element.getBoundingClientRect();
        setActiveEventDimensions({ width: rect.width, height: rect.height });
      }
    }
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const eventData = event.active.data.current?.['event'] as
        | CalendarEvent
        | undefined;
      if (!eventData) return;
      const update = getDraggedEventUpdate(eventData, event.delta);
      if (!update) return;
      setActiveEvent(prev => {
        if (
          prev &&
          prev.startTime === update.startTime &&
          prev.endTime === update.endTime
        ) {
          return prev;
        }
        return { ...eventData, ...update };
      });
    },
    [getDraggedEventUpdate]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event;
      
      // We don't clear activeEvent yet to avoid flash, we do it after state setup
      // activeEvent clearing happens at the end of this function or via state update

      if (!layerRef.current) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      const eventData = active.data.current?.['event'] as CalendarEvent | undefined;
      if (!eventData) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      const update = getDraggedEventUpdate(eventData, delta);
      if (!update) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      // 1. Optimistically commit the change locally
      const updatedEvent = { ...eventData, ...update };
      committedEventRef.current = updatedEvent;
      forceUpdate({}); // Trigger re-render to apply mergedEvents

      // 2. Clear overlay (now mergedEvents will take over rendering at new pos)
      setActiveEvent(null);
      setActiveEventDimensions(null);

      // 3. Trigger actual update
      onEventUpdate(updatedEvent);
    },
    [getDraggedEventUpdate, onEventUpdate]
  );

  const handleEventResize: EventResizeHandler = useCallback(
    (event: CalendarEvent, newEndTime: string) => {
      onEventUpdate({
        ...event,
        endTime: newEndTime,
      });
    },
    [onEventUpdate]
  );

  return (
    <DndContext
      sensors={sensors}
      autoScroll={calendarAutoScrollOptions}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      <div ref={layerRef} className={styles.eventLayer}>
        {mergedEvents.map(event => {
          const layout = layoutMap.get(event.id);
          return (
            <EventItem
              key={event.id}
              event={event}
              onEdit={onEventEdit}
              onResize={handleEventResize}
              renderEvent={renderEvent}
              column={layout?.column ?? 0}
              totalColumns={layout?.totalColumns ?? 1}
            />
          );
        })}
      </div>

      <DragOverlay>
        {activeEvent ? (
          <div
            style={{
              opacity: 1,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              cursor: 'grabbing',
              backgroundColor: renderEvent
                ? 'transparent'
                : 'var(--affine-background-primary-color)',
              borderRadius: '8px',
              width: activeEventDimensions?.width
                ? `${activeEventDimensions.width}px`
                : renderEvent
                  ? '280px'
                  : '200px',
              height: activeEventDimensions?.height
                ? `${activeEventDimensions.height}px`
                : (() => {
                    const startParts = activeEvent.startTime.split(':').map(Number);
                    const endParts = activeEvent.endTime.split(':').map(Number);
                    const startHour = startParts[0] ?? 0;
                    const startMinute = startParts[1] ?? 0;
                    const endHour = endParts[0] ?? 0;
                    const endMinute = endParts[1] ?? 0;
                    const startMinutes = startHour * 60 + startMinute;
                    const endMinutes = endHour * 60 + endMinute;
                    const durationMinutes = endMinutes - startMinutes;
                    const layerHeight =
                      layerRef.current?.getBoundingClientRect().height ||
                      24 * 40;
                    return `${(durationMinutes / (24 * 60)) * layerHeight}px`;
                  })(),
              minHeight: '16px',
              overflow: 'hidden',
            }}
          >
            <EventItem
              event={activeEvent}
              onEdit={() => {}}
              onResize={() => {}}
              isDragOverlay={true}
              renderEvent={renderEvent}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
